// frontend/src/components/Dashboard.js
import React from 'react';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, FunnelChart, Funnel,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell, LabelList 
} from 'recharts';

// Custom colors for charts - more modern palette
const COLORS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e',
  '#f97316', '#facc15', '#a3e635', '#10b981', '#06b6d4'
];

// QoE colors - red-orange gradient for negative metrics
const QOE_COLORS = [
  '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b',
  '#7f1d1d', '#b45309', '#b45309', '#a16207', '#854d0e'
];

function Dashboard({ result, loading }) {
  const { data, chartType, query, claudeInsight, intent, metadata = {} } = result;

  // Extract metadata for proper display
  const { metricUnit = '', metricType = 'positive' } = metadata;
  const isNegativeMetric = metricType === 'negative';

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  // Truncate long labels
  const truncateLabel = (label, maxLength = 12) => {
    if (!label) return 'Unknown';
    if (typeof label !== 'string') return String(label).substring(0, maxLength);
    
    // For long labels with spaces, try to find a good breaking point
    if (label.length > maxLength && label.includes(' ')) {
      const words = label.split(' ');
      let result = words[0];
      for (let i = 1; i < words.length; i++) {
        if ((result + ' ' + words[i]).length <= maxLength) {
          result += ' ' + words[i];
        } else {
          return result + '...';
        }
      }
      return result;
    }
    
    // Simple truncation for single words or when no good breaking point
    return label.length > maxLength ? label.substring(0, maxLength) + '...' : label;
  };

  // Sanitize data before rendering charts
  const sanitizeData = (rawData) => {
    if (!rawData) return [];
    if (!Array.isArray(rawData)) return [];
    
    return rawData.map(item => ({
      name: item.name || 'Unknown',
      value: Number.isFinite(item.value) ? item.value : 0
    }));
  };

  // Format labels properly - especially for pie charts
  const formatLabel = (name, percent) => {
    if (!name || name === 'undefined') return 'Unknown';
    return `${name}: ${(percent * 100).toFixed(0)}%`;
  };

  // Calculate percentage change - for insights
  const calculateChange = (data) => {
    if (!Array.isArray(data) || data.length < 2) return null;
    const sorted = [...data].sort((a, b) => b.value - a.value);
    const top = sorted[0].value;
    const runner = sorted[1].value;
    return ((top - runner) / runner * 100).toFixed(1);
  };

  // Add percentages for funnel data
  const addPercentages = (funnelData) => {
    if (!funnelData || funnelData.length === 0) return [];
    
    const firstValue = funnelData[0]?.value || 1;
    return funnelData.map((item, index) => ({
      ...item,
      percentage: ((item.value / firstValue) * 100).toFixed(1) + '%',
      displayValue: formatNumber(item.value)
    }));
  };

  const renderChart = () => {
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No data available</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try refining your query or choose a different question.</p>
          </div>
        </div>
      );
    }

    switch (chartType) {
      case 'bar':
        // Sanitize data
        const sanitizedBarData = sanitizeData(data);
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
              {intent === 'viewership' ? 'Viewership Distribution' : 
               intent === 'demographics' ? 'Demographic Breakdown' :
               intent === 'qoe' ? (
                 metricUnit === 'ms' ? 'Startup Times (ms)' : 
                 metricUnit === '%' && query.toLowerCase().includes('error') ? 'Error Rates (%)' :
                 'Buffering Rates (%)'
               ) :
               intent === 'performance' ? 'Content Performance' : 'Results'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={sanitizedBarData} 
                margin={{ top: 5, right: 30, left: 20, bottom: 150 }}
                barSize={40}
                maxBarSize={40}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={120}
                  tick={{ fontSize: 11, fill: "#e5e7eb" }}
                  tickFormatter={value => truncateLabel(value, 12)}
                  interval={0}
                  tickMargin={25}
                />
                <YAxis 
                  tickFormatter={formatNumber} 
                  tick={{ fill: "#e5e7eb" }}
                />
                <Tooltip 
                  formatter={(value) => [
                    `${formatNumber(value)}${metricUnit || ''}`, 
                    isNegativeMetric ? "Issue Level" : "Value"
                  ]} 
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px', border: '1px solid #f0f0f0' }}
                />
                <Legend wrapperStyle={{ paddingTop: 10 }} />
                <Bar 
                  dataKey="value" 
                  name={isNegativeMetric ? "Issue Level" : "Value"} 
                  radius={[4, 4, 0, 0]}
                  minPointSize={2}
                >
                  {sanitizedBarData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={isNegativeMetric ? QOE_COLORS[index % QOE_COLORS.length] : COLORS[index % COLORS.length]} 
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      
      case 'pie':
        // Apply sanitization to data before rendering
        const sanitizedPieData = sanitizeData(data);
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
              {intent === 'demographics' ? 'Demographic Distribution' : 'Percentage Breakdown'}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={sanitizedPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={150}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({name, percent}) => formatLabel(name, percent)}
                >
                  {sanitizedPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [value, 'Value']} 
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px', border: '1px solid #f0f0f0' }}
                />
                <Legend 
                  layout="vertical" 
                  verticalAlign="middle" 
                  align="right"
                  wrapperStyle={{ paddingLeft: 20 }}
                  formatter={(value) => value === 'undefined' ? 'Unknown' : value}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );
      
      case 'line':
        const sanitizedLineData = sanitizeData(data);
        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Trend Analysis</h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={sanitizedLineData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={80} 
                  tick={{ fontSize: 12, fill: "#e5e7eb" }}
                  tickFormatter={truncateLabel}
                  interval={0}
                  tickMargin={10}
                />
                <YAxis 
                  tickFormatter={formatNumber} 
                  tick={{ fill: "#e5e7eb" }}
                />
                <Tooltip 
                  formatter={(value) => [`${formatNumber(value)}${metricUnit || ''}`, "Value"]} 
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px', border: '1px solid #f0f0f0' }}
                />
                <Legend wrapperStyle={{ paddingTop: 10 }} />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#6366f1" 
                  strokeWidth={2}
                  activeDot={{ r: 8 }} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      
      case 'funnel':
        // Create proper funnel data format with percentages
        const funnelData = addPercentages([...data].map(item => ({
          value: item.value || 0,
          name: item.name || 'Unknown',
          fill: COLORS[data.indexOf(item) % COLORS.length]
        })));

        return (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Conversion Funnel</h3>
            <div className="mb-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-center">
                {funnelData.map((step, index) => (
                  <div key={index} className="bg-indigo-50 dark:bg-indigo-900/30 p-2 rounded">
                    <p className="text-xs text-indigo-500 dark:text-indigo-300">{step.name}</p>
                    <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300">{step.displayValue}</p>
                    <p className="text-sm text-indigo-500 dark:text-indigo-400">{step.percentage}</p>
                  </div>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <FunnelChart>
                <Tooltip 
                  formatter={(value) => [formatNumber(value), 'Count']} 
                  contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px', border: '1px solid #f0f0f0' }}
                />
                <Funnel
                  dataKey="value"
                  data={funnelData}
                  isAnimationActive
                >
                  <LabelList 
                    position="right" 
                    fill="currentColor" 
                    stroke="none" 
                    dataKey="name" 
                    className="text-gray-800 dark:text-gray-200"
                  />
                  <LabelList 
                    position="right" 
                    fill="currentColor" 
                    stroke="none" 
                    dataKey="displayValue" 
                    offset={90} 
                    className="text-gray-600 dark:text-gray-300"
                  />
                  <LabelList 
                    position="right" 
                    fill="currentColor" 
                    stroke="none" 
                    dataKey="percentage" 
                    offset={180} 
                    className="text-gray-600 dark:text-gray-400"
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        );
      
      case 'overview':
        // Sanitize all the overview data
        const sanitizedOverview = {
          topContent: sanitizeData(data.topContent || []),
          topPlatforms: sanitizeData(data.topPlatforms || []),
          topRegions: sanitizeData(data.topRegions || []),
          topCompletionRate: sanitizeData(data.topCompletionRate || [])
        };

        // Overview shows multiple charts in a grid
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Top Content by Viewership</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sanitizedOverview.topContent}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tickFormatter={truncateLabel}
                    tick={{ fill: "#e5e7eb" }}
                  />
                  <YAxis 
                    tickFormatter={formatNumber}
                    tick={{ fill: "#e5e7eb" }}
                  />
                  <Tooltip
                    formatter={(value) => [formatNumber(value), 'Viewers']}
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px', border: '1px solid #f0f0f0' }}
                  />
                  <Bar dataKey="value" name="Viewers" radius={[4, 4, 0, 0]}>
                    {sanitizedOverview.topContent.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Platform Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={sanitizedOverview.topPlatforms}
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    labelLine={false}
                    label={({name, percent}) => formatLabel(name, percent)}
                  >
                    {sanitizedOverview.topPlatforms.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatNumber(value), 'Viewers']} 
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Regional Viewership</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sanitizedOverview.topRegions}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tickFormatter={truncateLabel}
                    tick={{ fill: "#e5e7eb" }}
                  />
                  <YAxis 
                    tickFormatter={formatNumber}
                    tick={{ fill: "#e5e7eb" }}
                  />
                  <Tooltip 
                    formatter={(value) => [formatNumber(value), 'Viewers']} 
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px' }}
                  />
                  <Bar dataKey="value" name="Viewers" radius={[4, 4, 0, 0]}>
                    {sanitizedOverview.topRegions.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[(index + 3) % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">Content Completion Rate</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sanitizedOverview.topCompletionRate}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="name" 
                    tickFormatter={truncateLabel}
                    tick={{ fill: "#e5e7eb" }}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ fill: "#e5e7eb" }}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Completion Rate']} 
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '6px' }}
                  />
                  <Bar dataKey="value" name="Completion Rate (%)" radius={[4, 4, 0, 0]}>
                    {sanitizedOverview.topCompletionRate.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[(index + 6) % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400">No visualization available for this data type</p>
          </div>
        );
    }
  };

  // Create insight cards based on data and query type
  const renderInsightCards = () => {
    if (!data || (Array.isArray(data) && data.length === 0)) return null;
    
    // For standard chart types
    if (Array.isArray(data) && chartType !== 'overview') {
      const sanitizedData = sanitizeData(data);
      const sortedData = [...sanitizedData].sort((a, b) => b.value - a.value);
      const leader = sortedData[0] || { name: 'Unknown', value: 0 };
      const total = sortedData.reduce((sum, item) => sum + (item.value || 0), 0);
      const percentChange = calculateChange(sanitizedData);
      
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* First card - Leader or Critical Issue based on metric type */}
          <div className={`bg-white p-4 rounded-lg shadow-sm border-l-4 ${isNegativeMetric ? 'border-red-500' : 'border-indigo-500'} dark:bg-gray-800 dark:border-gray-700`}>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isNegativeMetric ? 'Critical Issue' : 'Leader'}
            </p>
            <p className="text-xl font-bold text-gray-800 dark:text-gray-100">{leader.name || 'Unknown'}</p>
            <p className="text-lg text-gray-700 dark:text-gray-300">
              {formatNumber(leader.value)}
              {metricUnit || (chartType === 'pie' ? '%' : '')}
            </p>
          </div>
          
          {/* Second card - Total Volume */}
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-emerald-500 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Total Volume</p>
            <p className="text-xl font-bold text-gray-800 dark:text-gray-100">{formatNumber(total)}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Across all data points</p>
          </div>
          
          {/* Third card - Leader's Advantage or Performance Gap */}
          {percentChange && (
            <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-amber-500 dark:bg-gray-800 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isNegativeMetric ? 'Performance Gap' : 'Leader\'s Advantage'}
              </p>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-100">{percentChange}%</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isNegativeMetric ? 'Above average level' : 'Over second place'}
              </p>
            </div>
          )}
        </div>
      );
    }
    
    // For overview dashboard
    if (chartType === 'overview' && data.topContent && data.topContent.length > 0) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-indigo-500 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Top Content</p>
            <p className="text-md font-bold text-gray-800 dark:text-gray-100">{data.topContent[0].name || 'Unknown'}</p>
            <p className="text-lg text-gray-700 dark:text-gray-300">{formatNumber(data.topContent[0].value)} viewers</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-rose-500 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Top Platform</p>
            <p className="text-md font-bold text-gray-800 dark:text-gray-100">{data.topPlatforms[0].name || 'Unknown'}</p>
            <p className="text-lg text-gray-700 dark:text-gray-300">{formatNumber(data.topPlatforms[0].value)} viewers</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-emerald-500 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Top Region</p>
            <p className="text-md font-bold text-gray-800 dark:text-gray-100">{data.topRegions[0].name || 'Unknown'}</p>
            <p className="text-lg text-gray-700 dark:text-gray-300">{formatNumber(data.topRegions[0].value)} viewers</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-amber-500 dark:bg-gray-800 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">Best Completion Rate</p>
            <p className="text-md font-bold text-gray-800 dark:text-gray-100">{data.topCompletionRate[0].name || 'Unknown'}</p>
            <p className="text-lg text-gray-700 dark:text-gray-300">{data.topCompletionRate[0].value}%</p>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="bg-gray-50 rounded-lg shadow-md p-6 dark:bg-gray-900 dark:border dark:border-gray-800">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center">
          Query Results 
          {loading && (
            <svg className="animate-spin ml-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mt-1 italic">"{query}"</p>
      </div>
      
      {/* Insight cards section */}
      {renderInsightCards()}
      
      {/* Claude's AI insight message */}
      {claudeInsight && (
        <div className={`bg-gradient-to-r ${isNegativeMetric ? 'from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30 border-l-4 border-red-500' : 'from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border-l-4 border-indigo-500'} p-4 rounded-md mb-6 flex`}>
          <div className="mr-3 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${isNegativeMetric ? 'text-red-600 dark:text-red-400' : 'text-indigo-600 dark:text-indigo-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className={`text-md font-semibold ${isNegativeMetric ? 'text-red-700 dark:text-red-300' : 'text-indigo-700 dark:text-indigo-300'} mb-1`}>AI Insight</h3>
            <p className={`${isNegativeMetric ? 'text-gray-700 dark:text-gray-300' : 'text-gray-700 dark:text-gray-300'}`}>{claudeInsight}</p>
          </div>
        </div>
      )}
      
      {/* Chart visualization */}
      <div className="mt-4">
        {renderChart()}
      </div>
    </div>
  );
}

export default Dashboard;
